"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CompactPageHeader() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tickerItems = ["PRECISION", "INDUSTRIAL", "LASER MARKING", "DURABLE", "EXCELLENCE", "PRECISION", "INDUSTRIAL", "LASER MARKING", "DURABLE", "EXCELLENCE"];
  return <section data-section-id="1285" id="compact-page-header" className="relative bg-background text-foreground min-h-screen w-full overflow-hidden flex items-center justify-center py-0">
      {}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_laser_engraving.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_laser_engraving.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_laser_engraving.png-wvc-sizes" alt="Industrial laser engraving process on metal surface" className="h-full w-full object-cover opacity-40" loading="eager" decoding="async" />
        <div className="absolute inset-0 bg-background/70" />
        {}
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
      }} />
      </div>

      {}
      <div className="absolute top-20 md:top-24 left-0 right-0 z-20 overflow-hidden border-y border-border">
        <div className="flex whitespace-nowrap py-3 animate-[ticker_40s_linear_infinite] hover:[animation-play-state:paused]">
          {tickerItems.concat(tickerItems).map((item, i) => <div key={i} data-index={i} className="flex items-center shrink-0">
              <span className="font-mono text-xs tracking-[0.18em] uppercase text-foreground px-6">
                {item}
              </span>
              <span className="size-1.5 bg-primary shrink-0" aria-hidden="true" />
            </div>)}
        </div>
      </div>

      {}
      <div aria-hidden="true" className="absolute bottom-0 -right-8 md:right-0 z-10 pointer-events-none select-none leading-none" style={{
      fontFamily: "var(--font-sans)",
      fontSize: "clamp(18rem, 32vw, 36rem)",
      color: "hsl(45 68% 47% / 0.06)",
      letterSpacing: "-0.02em"
    }}>
        01
      </div>

      {}
      <div className="relative z-30 w-full max-w-[1600px] px-6 md:px-12 lg:px-16 pt-32 pb-32 md:pt-40 md:pb-40">
        {}
        <div className="flex items-center gap-3 mb-12 md:mb-16">
          <span className="font-mono text-xs md:text-sm font-medium tracking-[0.18em] uppercase text-foreground">
            § 01 / Precision
          </span>
          <span className="h-px w-10 bg-border" aria-hidden="true" />
        </div>

        {}
        <div ref={headlineRef} className={`text-center transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h1 className="font-default font-normal uppercase text-foreground" style={{
          fontSize: "clamp(4rem, 13vw, 12rem)",
          letterSpacing: "-0.02em",
          lineHeight: "0.82"
        }}>
            <span className="block">Industrial</span>
            <span className="block">
              <span className="relative inline-block">
                <span className="font-serif italic text-primary" style={{
                fontSize: "0.85em",
                letterSpacing: "0.01em"
              }}>
                  Precision
                </span>
                {}
                <span aria-hidden="true" className="absolute left-0 right-0 -bottom-2 md:-bottom-3 origin-left flex items-center" style={{
                transform: "rotate(-6deg)"
              }}>
                  <span className={`h-px bg-primary transition-transform duration-700 ease-out origin-left ${headlineVisible ? "scale-x-100" : "scale-x-0"}`} style={{
                  width: "100%"
                }} />
                  <span className={`size-1.5 bg-primary shrink-0 transition-all duration-300 ${headlineVisible ? "opacity-100 scale-100" : "opacity-0 scale-0"}`} style={{
                  transitionDelay: headlineVisible ? "700ms" : "0ms"
                }} aria-hidden="true" />
                </span>
              </span>{" "}
              Marking
            </span>
          </h1>
        </div>

        {}
        <div className={`mt-10 md:mt-14 text-center transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionDelay: headlineVisible ? "160ms" : "0ms"
      }}>
          <p className="font-mono text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-foreground/80">
            Professional Laser Engraving &amp; Industrial Marking Solutions
          </p>
        </div>

        {}
        <div ref={ctaRef} className={`mt-12 md:mt-16 flex justify-center transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionDelay: ctaVisible ? "240ms" : "0ms"
      }}>
          <div className="relative group inline-block">
            <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 border-[1.5px] border-primary px-9 py-6 h-auto font-default uppercase tracking-[0.04em] text-base md:text-lg transition-all duration-[180ms] ease-out relative z-10 group-hover:translate-x-1 group-hover:-translate-y-1">
              <Link to="/ServicesPage">
                View Services
                <ArrowRight className="ml-1 size-5" strokeWidth={1.5} />
              </Link>
            </Button>
            {}
            <span aria-hidden="true" className="absolute inset-0 bg-foreground -z-0 opacity-0 group-hover:opacity-100 transition-all duration-[180ms] ease-out translate-x-0 translate-y-0 group-hover:translate-x-1 group-hover:translate-y-1" />
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none overflow-hidden">
        <div className="relative h-6 flex items-center" style={{
        transform: "rotate(-6deg) translateY(0)"
      }}>
          <span className="h-px bg-primary flex-1" />
          <span className="size-1.5 bg-primary shrink-0 mr-4" aria-hidden="true" />
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}